// Page Galerie Photo Mobile - Design unifié
// Header simple "Galerie" + Onglets Tous/Mes amis/Mes Pros + Loupe recherche fullscreen

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, X, Clock, Image, Heart, MessageCircle, Eye, Loader2,
  RefreshCw, SlidersHorizontal, User, Users, Briefcase
} from 'lucide-react';
import MobileBottomNav from '../../components/layout/MobileBottomNav';
import UnifiedFilterSystem from '../../components/filters/UnifiedFilterSystem';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Liste des métiers
const METIERS = [
  { value: 'tous', label: 'Tous', icon: '🏠' },
  { value: 'plombier', label: 'Plombier', icon: '💧' },
  { value: 'electricien', label: 'Électricien', icon: '⚡' },
  { value: 'macon', label: 'Maçon', icon: '🧱' },
  { value: 'carreleur', label: 'Carreleur', icon: '🔲' },
  { value: 'peintre', label: 'Peintre', icon: '🎨' },
  { value: 'menuisier', label: 'Menuisier', icon: '🔨' },
  { value: 'couvreur', label: 'Couvreur', icon: '🏠' },
  { value: 'chauffagiste', label: 'Chauffagiste', icon: '🔥' },
  { value: 'pisciniste', label: 'Pisciniste', icon: '🏊' },
  { value: 'paysagiste', label: 'Paysagiste', icon: '🌿' },
];

// Composant Photo Card
function PhotoCard({ photo, onClick }) {
  const [liked, setLiked] = useState(false);
  const author = photo.author || {};
  const isPro = author.is_pro || author.type === 'pro' || photo.is_pro;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative group rounded-xl overflow-hidden bg-gray-100 cursor-pointer"
      onClick={() => onClick(photo)}
    >
      <img 
        src={photo.image || photo.thumbnail || photo.photos?.[0]} 
        alt={photo.title || 'Photo'}
        className="w-full aspect-square object-cover"
        loading="lazy"
      />
      
      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
        <div className="flex items-center gap-1 text-white">
          <Heart size={18} fill={liked ? 'white' : 'none'} />
          <span className="text-sm font-medium">{photo.likes || 0}</span>
        </div>
        <div className="flex items-center gap-1 text-white">
          <MessageCircle size={18} />
          <span className="text-sm font-medium">{photo.comments || 0}</span>
        </div>
      </div>
      
      {isPro && (
        <div className="absolute top-2 right-2 px-2 py-1 bg-[#1E3A5F] text-white rounded-full text-[10px] font-bold flex items-center gap-1">
          <Briefcase size={10} /> PRO
        </div>
      )}
      
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center gap-2">
          {author.avatar ? (
            <img src={author.avatar} alt="" className="w-6 h-6 rounded-full object-cover" />
          ) : (
            <div className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ background: author.color || '#F97316' }}>
              {(author.name || 'A')[0]}
            </div>
          )}
          <span className="text-white text-xs font-medium truncate">{author.name || 'Artisan'}</span>
        </div>
      </div>
    </motion.div>
  );
}

// Modal photo
function PhotoModal({ photo, onClose }) {
  if (!photo) return null;
  const author = photo.author || {};
  const isPro = author.is_pro || author.type === 'pro' || photo.is_pro;
  
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/90 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between p-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          {author.avatar ? (
            <img src={author.avatar} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold" style={{ background: author.color || '#F97316' }}>
              {(author.name || 'A')[0]}
            </div>
          )}
          <div>
            <div className="flex items-center gap-2">
              <p className="text-white font-medium">{author.name || 'Artisan'}</p>
              {isPro && <span className="px-1.5 py-0.5 bg-[#1E3A5F] text-white rounded text-[10px] font-bold">PRO</span>}
            </div>
            <p className="text-white/60 text-sm">{author.metier || photo.metier || ''}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full">
          <X size={24} className="text-white" />
        </button>
      </div>
      
      <div className="flex-1 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
        <img src={photo.image || photo.thumbnail || photo.photos?.[0]} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
      </div>
      
      <div className="p-4 flex items-center justify-center gap-8" onClick={(e) => e.stopPropagation()}>
        <button className="flex items-center gap-2 text-white hover:text-red-400"><Heart size={24} /><span>{photo.likes || 0}</span></button>
        <button className="flex items-center gap-2 text-white hover:text-blue-400"><MessageCircle size={24} /><span>{photo.comments || 0}</span></button>
        <button className="flex items-center gap-2 text-white hover:text-green-400"><Eye size={24} /><span>{photo.views || 0}</span></button>
      </div>
    </motion.div>
  );
}

export default function MobileGaleriePage({ isDesktopEmbed = false }) {
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  
  // Filtres
  const [activeTab, setActiveTab] = useState('tous');
  const [showFullscreenSearch, setShowFullscreenSearch] = useState(false);
  const [showFullFilters, setShowFullFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeFilters, setActiveFilters] = useState({});
  const searchInputRef = useRef(null);
  
  useEffect(() => {
    fetchPhotos();
  }, []);
  
  useEffect(() => {
    try {
      const saved = localStorage.getItem('galerie_recent_searches');
      if (saved) setRecentSearches(JSON.parse(saved).slice(0, 5));
    } catch {}
  }, []);
  
  useEffect(() => {
    if (showFullscreenSearch && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 250);
    }
  }, [showFullscreenSearch]);
  
  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/reseau/galerie?limit=50`);
      if (res.ok) {
        const data = await res.json();
        setPhotos(data.galerie || []);
      } else {
        const postsRes = await fetch(`${API_URL}/api/reseau/posts?limit=50`);
        if (postsRes.ok) {
          const postsData = await postsRes.json();
          const photoPosts = (postsData.posts || [])
            .filter(p => p.images?.length > 0 || p.thumbnail)
            .map(p => ({
              id: p.id, image: p.images?.[0] || p.thumbnail, photos: p.images,
              author: p.author, metier: p.author?.metier || p.metier,
              likes: p.likes, comments: p.comments?.length || 0,
              is_pro: p.author?.is_pro || p.author?.type === 'pro' || p.is_pro
            }));
          setPhotos(photoPosts);
        }
      }
    } catch (err) {
      console.error('Erreur:', err);
    }
    setLoading(false);
  }, []);
  
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchPhotos();
    setRefreshing(false);
  };
  
  const saveRecentSearch = (query) => {
    if (!query || query.trim().length < 2) return;
    const trimmed = query.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())].slice(0, 5);
    setRecentSearches(updated);
    try { localStorage.setItem('galerie_recent_searches', JSON.stringify(updated)); } catch {}
  };
  
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try { localStorage.removeItem('galerie_recent_searches'); } catch {}
  };
  
  const handleSearchSubmit = (query) => {
    if (query && query.trim()) {
      saveRecentSearch(query);
      setSearchQuery(query.trim());
    }
    setShowFullscreenSearch(false);
  };
  
  // Filtrer les photos
  const filteredPhotos = photos.filter(p => {
    if (activeTab === 'mes_amis') return p.author?.is_friend && !p.author?.is_pro;
    if (activeTab === 'mes_pros') return (p.author?.is_pro || p.author?.type === 'pro' || p.is_pro) && p.author?.is_followed;
    return true;
  });
  
  return (
    <div className={isDesktopEmbed ? "bg-white" : "min-h-screen bg-gray-50"} style={{ paddingBottom: isDesktopEmbed ? '0' : '80px' }}>
      {/* Barre Bêta + Header - Masqués en mode Desktop embed */}
      {!isDesktopEmbed && (
        <>
          {/* Bandeau bêta supprimé - géré par TopBanner.jsx global */}
          
          <header className="bg-white border-b border-gray-200 sticky top-[30px] z-50">
            <div className="flex items-center justify-between h-14 px-3">
              <div className="flex items-center gap-0.5">
                <h1 className="text-lg font-bold text-[#1E3A5F]">Galerie</h1>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={handleRefresh} disabled={refreshing} className="p-2 hover:bg-gray-100 rounded-full">
                  <RefreshCw size={20} className={`text-gray-600 ${refreshing ? 'animate-spin' : ''}`} />
                </button>
                <button onClick={() => setShowFullscreenSearch(true)} className="p-2 hover:bg-gray-100 rounded-full" data-testid="galerie-search-btn">
                  <Search size={20} className="text-gray-600" />
                </button>
              </div>
            </div>
            
            <div className="flex border-b border-gray-100">
              <button onClick={() => setActiveTab('tous')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'tous' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}>
                <Users size={14} /> Tous
              </button>
              <button onClick={() => setActiveTab('mes_amis')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'mes_amis' ? 'text-orange-600 border-b-2 border-orange-500' : 'text-gray-500'}`}>
                <User size={14} /> Mes amis
              </button>
              <button onClick={() => setActiveTab('mes_pros')} className={`flex-1 py-2.5 text-xs font-medium flex items-center justify-center gap-1.5 ${activeTab === 'mes_pros' ? 'text-[#1E3A5F] border-b-2 border-[#1E3A5F]' : 'text-gray-500'}`}>
                <Briefcase size={14} /> Mes Pros
              </button>
            </div>
          </header>
        </>
      )}
      
      {/* Contenu */}
      <div className="p-2">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>
        ) : filteredPhotos.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center px-4">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              {activeTab === 'mes_pros' ? <Briefcase size={32} className="text-gray-400" /> : activeTab === 'mes_amis' ? <User size={32} className="text-gray-400" /> : <Image size={32} className="text-gray-400" />}
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              {activeTab === 'mes_pros' ? 'Aucune photo de tes pros' : activeTab === 'mes_amis' ? 'Aucune photo de tes amis' : 'Aucune photo'}
            </h3>
            <p className="text-sm text-gray-500">
              {activeTab === 'mes_pros' ? 'Suis des professionnels pour voir leurs réalisations' : activeTab === 'mes_amis' ? 'Tes amis n\'ont pas encore partagé de photos' : 'Sois le premier à partager !'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {filteredPhotos.map((photo, index) => (
              <PhotoCard key={photo.id || index} photo={photo} onClick={setSelectedPhoto} />
            ))}
          </div>
        )}
      </div>
      
      {/* Recherche Fullscreen */}
      <AnimatePresence>
        {showFullscreenSearch && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: '#ffffff', zIndex: 99999 }}>
            <div className="flex items-center justify-end" style={{ padding: '8px 12px', paddingTop: 'max(8px, env(safe-area-inset-top, 8px))' }}>
              <button onClick={() => setShowFullscreenSearch(false)} style={{ width: '32px', height: '32px' }}>
                <X size={24} className="text-[#64748B]" />
              </button>
            </div>
            
            <div className="px-3 pb-3">
              <form onSubmit={(e) => { e.preventDefault(); handleSearchSubmit(searchQuery); }} className="flex items-center w-full" style={{ height: '56px', border: '2px solid #E8650A', borderRadius: '28px', background: '#F8F9FA', overflow: 'hidden' }}>
                <motion.button type="submit" initial={{ x: 320, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: 'spring', stiffness: 280, damping: 24, delay: 0.03 }} className="flex items-center justify-center flex-shrink-0" style={{ width: '56px', height: '56px', background: '#E8650A', borderRadius: '26px 0 0 26px', position: 'relative', left: '-2px' }}>
                  <Search size={24} className="text-white" />
                </motion.button>
                <input ref={searchInputRef} type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Métier, artisan, style..." className="flex-1 bg-transparent text-[#333] placeholder-[#999] focus:outline-none" style={{ fontSize: '16px', paddingLeft: '12px', paddingRight: '16px' }} autoFocus />
                {searchQuery && (
                  <button type="button" onClick={() => setSearchQuery('')} className="flex items-center justify-center mr-4" style={{ width: '22px', height: '22px', background: '#C9CDD4', borderRadius: '50%' }}>
                    <X size={14} className="text-white" />
                  </button>
                )}
              </form>
            </div>
            
            <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="px-4 py-4">
              <button onClick={() => { setShowFullscreenSearch(false); setTimeout(() => setShowFullFilters(true), 150); }} className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl font-semibold text-white" style={{ background: 'linear-gradient(135deg, #1E3A5F 0%, #2D4A6F 100%)', boxShadow: '0 4px 14px rgba(30, 58, 95, 0.25)' }}>
                <SlidersHorizontal size={20} />
                <span style={{ fontSize: '15px' }}>Trouve ta photo en 3 clics</span>
              </button>
            </motion.div>
            
            <div className="p-4 bg-white flex-1 overflow-y-auto">
              <p className="text-[11px] text-[#E8650A] uppercase font-semibold tracking-wide mb-4">Recherche dans : Galerie</p>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold">Recherches récentes</p>
                  {recentSearches.length > 0 && <button onClick={clearRecentSearches} className="text-[11px] text-[#E8650A] font-medium">Effacer</button>}
                </div>
                {recentSearches.length > 0 ? (
                  <div className="space-y-1">
                    {recentSearches.map((search, idx) => (
                      <button key={idx} onClick={() => handleSearchSubmit(search)} className="w-full flex items-center gap-3 py-3 px-2 hover:bg-gray-50 rounded-lg">
                        <Clock size={16} className="text-[#9CA3AF]" />
                        <span className="text-[14px] text-[#374151]">{search}</span>
                      </button>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Search size={32} className="text-[#E5E7EB] mb-3" />
                    <p className="text-[#9CA3AF] text-sm">Aucune recherche récente</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Modal photo */}
      <AnimatePresence>
        {selectedPhoto && <PhotoModal photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} />}
      </AnimatePresence>
      
      {/* UnifiedFilterSystem */}
      <UnifiedFilterSystem context="galerie" isOpen={showFullFilters} onClose={() => setShowFullFilters(false)} filters={activeFilters} onFiltersChange={setActiveFilters} resultsCount={filteredPhotos.length} onApply={() => setShowFullFilters(false)} />
      
      {/* MobileBottomNav est géré globalement par MobileNavWrapper dans App.js */}
    </div>
  );
}
